import { useState, useEffect } from 'react'
import { Globe, X, ChevronDown, ChevronUp, List, Network } from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import type { AdGenJob } from '@/services/ad-generator/types'
import { adGeneratorService } from '@/services/ad-generator'
import StageCard from './components/StageCard'
import PhaseProgressBar, { PHASE_CONFIG } from './components/PhaseProgressBar'
import CapabilityBadges, { parsePreflightCapabilities } from './components/CapabilityBadges'
import BrandIntelCard, {
  parseBrandFromArtifact,
  parsePatternsFromArtifact,
  parseAdLibraryFromArtifact,
} from './components/BrandIntelCard'
import { PipelineGraph } from '@/components/pipeline-graph'

interface AdGeneratorPipelineProps {
  job: AdGenJob
  onCancel: () => void
  onViewStage?: (stageName: string) => void
}

type ViewMode = 'linear' | 'graph'

export default function AdGeneratorPipeline({ job, onCancel, onViewStage }: AdGeneratorPipelineProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('linear')
  const [showDetailedStages, setShowDetailedStages] = useState(false)
  const [artifacts, setArtifacts] = useState<Record<string, Record<string, unknown>>>({})
  const [, setLoadingArtifacts] = useState(false)

  const domain = (() => {
    try { return new URL(job.url).hostname.replace('www.', '') }
    catch { return job.url }
  })()

  const completedCount = job.stages.filter(
    s => s.status === 'completed' || s.status === 'degraded' || s.status === 'skipped'
  ).length

  // Load artifacts for completed stages to show insights
  useEffect(() => {
    const fetchArtifacts = async () => {
      const stagesToFetch = ['preflight_validation', 'brand_discovery', 'brand_ad_collection', 'competitor_intelligence']
      const completedStages = job.stages.filter(
        s => stagesToFetch.includes(s.name) && (s.status === 'completed' || s.status === 'degraded')
      )

      if (completedStages.length === 0) return

      setLoadingArtifacts(true)
      const newArtifacts: Record<string, Record<string, unknown>> = {}

      for (const stage of completedStages) {
        if (artifacts[stage.name]) continue // Skip already loaded
        try {
          const detail = await adGeneratorService.getStageDetail(job.job_id, stage.name)
          if (detail.artifact) {
            newArtifacts[stage.name] = detail.artifact
          }
        } catch (err) {
          console.error(`[Pipeline] Failed to load artifact for ${stage.name}:`, err)
        }
      }

      if (Object.keys(newArtifacts).length > 0) {
        setArtifacts(prev => ({ ...prev, ...newArtifacts }))
      }
      setLoadingArtifacts(false)
    }

    fetchArtifacts()
  }, [job.job_id, job.stages])

  // Parse artifacts for display
  const capabilities = parsePreflightCapabilities(artifacts.preflight_validation)
  const brand = parseBrandFromArtifact(artifacts.brand_discovery)
  const patterns = parsePatternsFromArtifact(artifacts.competitor_intelligence)
  const adLibrary = parseAdLibraryFromArtifact(artifacts.brand_ad_collection)

  // Determine current phase
  const isUnderstandPhaseComplete = PHASE_CONFIG[0].stages.every(stageName => {
    const stage = job.stages.find(s => s.name === stageName)
    return stage && (stage.status === 'completed' || stage.status === 'degraded' || stage.status === 'skipped')
  })

  return (
    <div className="flex-1 flex flex-col max-w-2xl mx-auto w-full px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <Globe className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-tf-text">Generating ad for {domain}</h2>
            <p className="text-xs text-tf-text-muted">
              Stage {completedCount} of {job.stages.length}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* View mode toggle */}
          <div className="flex items-center rounded-lg border border-tf-border overflow-hidden">
            <button
              onClick={() => setViewMode('linear')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors',
                viewMode === 'linear'
                  ? 'bg-blue-500/10 text-blue-500'
                  : 'text-tf-text-muted hover:bg-tf-bg-muted',
              )}
            >
              <List className="w-3.5 h-3.5" />
              Linear
            </button>
            <button
              onClick={() => setViewMode('graph')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors',
                viewMode === 'graph'
                  ? 'bg-blue-500/10 text-blue-500'
                  : 'text-tf-text-muted hover:bg-tf-bg-muted',
              )}
            >
              <Network className="w-3.5 h-3.5" />
              Graph
            </button>
          </div>
          {job.status === 'running' && (
            <button
              onClick={onCancel}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-500 hover:bg-red-500/10 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Graph View */}
      {viewMode === 'graph' && (
        <div className="flex-1 rounded-xl border border-tf-border overflow-hidden bg-tf-bg-card" style={{ minHeight: '500px' }}>
          <PipelineGraph
            stages={job.stages}
            currentStage={job.current_stage}
            jobId={job.job_id}
            jobStatus={job.status}
            className="h-full"
          />
        </div>
      )}

      {/* Linear View */}
      {viewMode === 'linear' && (
        <>
          {/* 4-Phase Progress */}
          <div className="mb-6 p-4 rounded-xl bg-tf-bg-card border border-tf-border">
            <PhaseProgressBar
              stages={job.stages}
              currentStage={job.current_stage}
              jobStatus={job.status}
            />
          </div>

          {/* Capability Badges (after preflight) */}
          {capabilities.length > 0 && (
            <div className="mb-4">
              <CapabilityBadges capabilities={capabilities} />
            </div>
          )}

          {/* Brand Intelligence Card (after understand phase) */}
          {brand && isUnderstandPhaseComplete && (
            <div className="mb-4">
              <BrandIntelCard
                brand={brand}
                patterns={patterns || undefined}
                adLibrary={adLibrary || undefined}
                isConfirmed={true}
              />
            </div>
          )}

          {/* Progress bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-tf-text-muted">Overall Progress</span>
              <span className="text-xs font-bold text-blue-500">{job.progress_pct}%</span>
            </div>
            <div className="h-2 bg-tf-bg-muted rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-700 ease-out',
                  job.status === 'failed' ? 'bg-red-500' :
                  job.status === 'completed' ? 'bg-emerald-500' :
                  'bg-blue-500',
                )}
                style={{ width: `${job.progress_pct}%` }}
              />
            </div>
          </div>

          {/* Live insight */}
          {job.status === 'running' && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
              <p className="text-xs text-blue-400">
                {getCurrentInsight(job)}
              </p>
            </div>
          )}

          {/* Error message */}
          {job.status === 'failed' && job.error_message && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/5 border border-red-500/20">
              <p className="text-xs text-red-400">{job.error_message}</p>
            </div>
          )}

          {/* Detailed stages toggle */}
          <button
            onClick={() => setShowDetailedStages(!showDetailedStages)}
            className="flex items-center justify-center gap-2 py-2 text-xs text-tf-text-muted hover:text-tf-text transition-colors"
          >
            {showDetailedStages ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Hide detailed stages
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Show detailed stages ({completedCount}/{job.stages.length})
              </>
            )}
          </button>

          {/* Stage list (collapsible) */}
          {showDetailedStages && (
            <div className="space-y-2 mt-2 flex-1 overflow-auto">
              {job.stages.map((stage, idx) => (
                <StageCard
                  key={stage.name}
                  stage={stage}
                  index={idx}
                  isActive={stage.name === job.current_stage}
                  onViewDetail={onViewStage ? () => onViewStage(stage.name) : undefined}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function getCurrentInsight(job: AdGenJob): string {
  // Find the most recent completed stage with an insight
  const completedStages = [...job.stages]
    .filter(s => s.insight && (s.status === 'completed' || s.status === 'degraded'))
    .reverse()

  if (completedStages.length > 0) {
    return completedStages[0].insight!
  }

  // Find running stage
  const running = job.stages.find(s => s.status === 'running')
  if (running) {
    return `Analyzing ${running.display_name.toLowerCase()}...`
  }

  return 'Processing...'
}
